import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  PAGE_SEO,
  ORG_LD,
  WEBSITE_LD,
} from "@/seo/site.config";

/**
 * <Seo path="/features" jsonLd={[...]} />
 *
 * - Looks up per-page metadata from PAGE_SEO[path].
 * - Injects Organization + WebSite schema sitewide.
 * - Accepts additional JSON-LD blocks (FAQPage, Service, BreadcrumbList, etc.).
 */
export default function Seo({ path, jsonLd = [], image, title, description }) {
  const cfg = PAGE_SEO[path] || {};
  const resolvedTitle = title || cfg.title || SITE_NAME;
  const resolvedDescription = description || cfg.description || "";
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const ogType = cfg.ogType || "website";

  // BreadcrumbList is auto-generated from PAGE_SEO[path].breadcrumbs
  const breadcrumbLd = cfg.breadcrumbs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: cfg.breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url,
        })),
      }
    : null;

  const allLd = [ORG_LD, WEBSITE_LD, breadcrumbLd, ...jsonLd].filter(Boolean);

  return (
    <Helmet>
      <html lang="en" />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {cfg.keywords && <meta name="keywords" content={cfg.keywords} />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Robots / AI crawlers — explicitly allow all */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />

      {/* JSON-LD */}
      {allLd.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}
